import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { FieldData, FieldDataValue } from '../../shared/types';
import uuid from 'uuid';
import { formatDate } from '../../lib/utils';

const TEXT_OPTIONS = ['short_text', 'long_text', 'url'];
const NUMBER_OPTIONS = ['number', 'number_range'];
const DATE_OPTIONS = ['date', 'date_range'];
const CURRENCY_OPTIONS = ['currency', 'currency_range'];
const SELECT_OPTIONS = ['yes_no', 'single_select', 'multi_select', 'user'];

class FieldObject {
  customField: Item | null;
  selection: FieldDataValue;
  currency: Item | null;
  min: null | string | Date;
  max: null | string | Date;
  reactKey: string;
  userDateFormat: string;

  constructor(userDateFormat: string, fieldData?: FieldData) {
    this.userDateFormat = userDateFormat;
    this.customField = fieldData?.customField || null;
    this.selection = fieldData?.selection || null;
    this.currency = fieldData?.currency || null;
    this.min = this.minMaxFromFieldData(fieldData?.min);
    this.max = this.minMaxFromFieldData(fieldData?.max);
    this.reactKey = uuid.v4();
  }

  minMaxFromFieldData(minMax: string | null | undefined | Date) {
    if (!minMax) {
      return null;
    }

    if (DATE_OPTIONS.includes(this.valueType()) && typeof minMax === 'string') {
      return new Date(minMax);
    }

    return minMax;
  }

  isValid() {
    if (!this.customField) {
      return false;
    }

    if ([...TEXT_OPTIONS, ...SELECT_OPTIONS].includes(this.valueType())) {
      return !!this.selection;
    }

    if (CURRENCY_OPTIONS.includes(this.valueType())) {
      return this.currency && this.minMaxValid();
    }

    if ([...NUMBER_OPTIONS, ...DATE_OPTIONS].includes(this.valueType())) {
      return this.minMaxValid();
    }
  }

  minMaxValid() {
    return (
      (this.min !== null && this.min !== '') ||
      (this.max !== null && this.max !== '')
    );
  }

  // sets the actual custom field
  setCustomField(item: Item | null) {
    this.clear();
    this.customField = item;
  }

  // sets the custom field option selection
  setSelection(selection: FieldDataValue) {
    this.selection = selection;
  }

  setCurrency(currency: Item | null) {
    this.currency = currency;
  }

  setMin(min: string | Date | null) {
    this.min = min;
  }

  setMax(max: string | Date | null) {
    this.max = max;
  }

  clear() {
    this.customField = null;
    this.selection = null;
    this.currency = null;
    this.min = null;
    this.max = null;
  }

  valueType() {
    return this.customField?.value.split('__')[1] || '';
  }

  isTextOption() {
    return TEXT_OPTIONS.includes(this.valueType());
  }

  isNumberOption() {
    return NUMBER_OPTIONS.includes(this.valueType());
  }

  isDateOption() {
    return this.valueType() === 'date';
  }

  isCurrencyOption() {
    return CURRENCY_OPTIONS.includes(this.valueType());
  }

  isYesNoOption() {
    return this.valueType() === 'yes_no';
  }

  isSelectOption() {
    return !this.isYesNoOption() && SELECT_OPTIONS.includes(this.valueType());
  }

  fieldId() {
    return this.customField?.value.split('__')[0];
  }

  fieldLabel() {
    return this.customField?.label || '';
  }

  queryParamKey(): `cf_${string}` {
    return `cf_${this.fieldId()}`;
  }

  queryParamValue() {
    if (!this.isValid()) {
      return '';
    }

    if (TEXT_OPTIONS.includes(this.valueType())) {
      return this.selection as string;
    }

    if (CURRENCY_OPTIONS.includes(this.valueType())) {
      if (!this.currency) {
        return '';
      }

      const start = this.min || '';
      const end = this.max || '';

      return `${this.currency.value}$${start}:${end}`;
    }

    if (NUMBER_OPTIONS.includes(this.valueType())) {
      const start = this.min || '';
      const end = this.max || '';

      return `${start}:${end}`;
    }

    if (DATE_OPTIONS.includes(this.valueType())) {
      const start = this.min ? new Date(this.min) : null;
      const end = this.max ? new Date(this.max) : null;
      return (
        `${formatDate(start, this.userDateFormat) || ''}:` +
        `${formatDate(end, this.userDateFormat) || ''}`
      );
    }

    if (SELECT_OPTIONS.includes(this.valueType())) {
      const item = this.selection as Item;
      return item.value;
    }

    return '';
  }

  static emptyField(userDateFormat = 'MM/dd/yyyy') {
    return new FieldObject(userDateFormat);
  }
}

export default FieldObject;
